import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.number.constructor.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.string.search.js";
import "core-js/modules/web.dom-collections.for-each.js";
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import track from 'utils/track';
import { useFetchMyPlaylistContent, useFetchMyPlaylistDetails, playlistQueryKeys } from 'actions/playlists';
import useBackLink, { slicePathSegments } from 'hooks/useBackLink';
import Box from 'components/Box';
import Card from 'components/Card';
import PageTitle from 'components/PageTitle';
import ResponsiveUserBadgeImage from 'components/UserBadgeImage/ResponsiveUserBadgeImage';
import PageSection from 'components/PageSection';
import PlaylistHeader from 'components/PlaylistHeader';
import EmptyStateController from 'components/EmptyStateController';
import ShareContentModal, { useGetSharingPermissions } from 'screens/ShareModal';
import { ADD_PLAYLIST_QUERY_PARAM } from '../myTeam/teamPlaylists';
import { BADGE_SIZE_LARGE, BADGE_SIZE_SMALL, PlaylistBannerBadge, Section, Sidebar, SidebarWrapper } from './Layout';
import Actions from './Actions';
import InfoSection from './InfoSection';
import PlaylistTabs from './PlaylistTabs';
import PlaylistComments from './PlaylistComments';
import PlaylistContent from './PlaylistContent';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function PlaylistPage() {
  var _playlistDetails$badg;
  var params = useParams();
  var playlistId = Number(params.id);
  var queryClient = useQueryClient();
  var _useFetchMyPlaylistDe = useFetchMyPlaylistDetails({
      id: playlistId
    }),
    playlistDetails = _useFetchMyPlaylistDe.data,
    detailsLoading = _useFetchMyPlaylistDe.isLoading,
    detailsError = _useFetchMyPlaylistDe.isError;
  var _useFetchMyPlaylistCo = useFetchMyPlaylistContent({
      id: playlistId
    }),
    content = _useFetchMyPlaylistCo.items,
    contentLoading = _useFetchMyPlaylistCo.isLoading,
    contentError = _useFetchMyPlaylistCo.isError;
  var isLoading = detailsLoading || contentLoading;
  var isError = detailsError || contentError;
  var playlist = useMemo(function () {
    return !playlistDetails || !content ? undefined : _objectSpread(_objectSpread({}, playlistDetails), {}, {
      content: content
    });
  }, [content, playlistDetails]);
  useEffect(function () {
    if (playlistId) {
      // Track playlist open event after 500ms
      var timerId = setTimeout(function () {
        track('collection', 'open', +playlistId);
        // Update the playlist status in parent playlists
        queryClient.invalidateQueries({
          queryKey: playlistQueryKeys.mySinglePlaylistContent(playlistId)
        });
      }, 500);
      return function () {
        return clearTimeout(timerId);
      };
    }
  }, [playlistId, queryClient]);
  var location = useLocation();
  var backLink = useBackLink({
    fallback: '/home',
    basePath: [slicePathSegments(location.pathname, 0, 2), '/user'],
    shouldSkipLocation: function shouldSkipLocation(loc) {
      var search = typeof loc.search === 'string' ? loc.search : '';
      return search.indexOf(ADD_PLAYLIST_QUERY_PARAM) !== -1;
    }
  });
  var _useGetSharingPermiss = useGetSharingPermissions({
      name: playlistDetails === null || playlistDetails === void 0 ? void 0 : playlistDetails.name,
      contentIds: playlistDetails !== null && playlistDetails !== void 0 && playlistDetails.id ? [playlistDetails.id] : [],
      playlistsToShare: playlistDetails ? [playlistDetails] : undefined
    }),
    shareData = _useGetSharingPermiss.shareData;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasShareModal = _useState2[0],
    setHasShareModal = _useState2[1];
  var toggleShareModal = function toggleShareModal() {
    return setHasShareModal(function (on) {
      return !on;
    });
  };
  var hasBadge = !!(playlistDetails !== null && playlistDetails !== void 0 && playlistDetails.badge && !(playlistDetails !== null && playlistDetails !== void 0 && (_playlistDetails$badg = playlistDetails.badge) !== null && _playlistDetails$badg !== void 0 && _playlistDetails$badg.isDeleted));
  return /*#__PURE__*/_jsxs(PageSection, {
    children: [hasShareModal && playlistDetails && /*#__PURE__*/_jsx(ShareContentModal, {
      data: shareData,
      onClose: toggleShareModal
    }), /*#__PURE__*/_jsxs(Card, {
      mb: "m",
      children: [/*#__PURE__*/_jsx(PlaylistHeader, {
        thumbnailId: playlist === null || playlist === void 0 ? void 0 : playlist.thumbnailId,
        isReady: !detailsLoading,
        backLink: backLink
      }), /*#__PURE__*/_jsxs(Section, {
        position: "relative",
        children: [playlistDetails && hasBadge && /*#__PURE__*/_jsx(PlaylistBannerBadge, {
          children: /*#__PURE__*/_jsx(ResponsiveUserBadgeImage, {
            badge: playlistDetails.badge,
            hasUserBadge: playlistDetails.hasUserBadge,
            sizes: [BADGE_SIZE_SMALL, BADGE_SIZE_LARGE],
            breakpoints: ['base', 'xs']
          })
        }), /*#__PURE__*/_jsxs(SidebarWrapper, {
          children: [/*#__PURE__*/_jsxs(Box, {
            grow: true,
            shrink: true,
            children: [/*#__PURE__*/_jsx(Box
            // Prevent title overlapping badge
            , {
              mr: [hasBadge ? BADGE_SIZE_SMALL : undefined, 0],
              breakpoints: "xs",
              children: /*#__PURE__*/_jsx(PageTitle, {
                size: "large",
                mb: "xs",
                children: (playlist === null || playlist === void 0 ? void 0 : playlist.name) || ''
              })
            }), playlistDetails && /*#__PURE__*/_jsx(InfoSection, {
              playlist: playlistDetails
            })]
          }), /*#__PURE__*/_jsx(Sidebar, {
            makeSpaceForBadge: hasBadge,
            children: playlistDetails && /*#__PURE__*/_jsx(Actions, {
              playlist: playlistDetails,
              toggleShareModal: toggleShareModal
            })
          })]
        })]
      }), /*#__PURE__*/_jsx(Section, {
        children: /*#__PURE__*/_jsx(EmptyStateController, {
          isLoading: isLoading,
          children: playlist && /*#__PURE__*/_jsx(PlaylistTabs, {
            renderContent: function renderContent() {
              return /*#__PURE__*/_jsx(PlaylistContent, {
                playlist: playlist,
                isError: isError
              });
            },
            renderComments: function renderComments() {
              return /*#__PURE__*/_jsx(PlaylistComments, {
                playlist: playlist
              });
            }
          })
        })
      })]
    })]
  });
}
export default PlaylistPage;